import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import StableLabel from './StableLabel';
import { stripAcutes, isVowel, toggleAcuteAt, sameMarks } from '../utils/stress';
import { useTtsPreview, slotsLabel } from '../utils/ttsPreview';
import { buildTtsRequest, loadLastTtsSettings } from './SpeechGeneration';
import { speakerOfPrefix, withSpeaker } from '../utils/wordMap';
import './SelectionPopup.css';

// What a selection in the prose can be acted on with, right where it was made.
//
// Any selection: hear it, or voice just it (the take comes back as a donor in
// the editor — see AudioStudio.newTake).
//
// A single word, additionally: set its stress — on this one place only, or
// as the quest's glossary rule for every place that has no mark of its own —
// and then hear how that same word is read in EVERY place it occurs across
// the quest, marking each one «says it as written» or not. The marks are the
// queue of what still wants re-voicing; they are kept per take and per
// spelling, so moving a stress mark asks for a fresh listen.

const fmt = (t) => {
  if (!Number.isFinite(t)) return '';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const rowKey = (r) => `${r.versionId || `${r.locationUuid}/${r.fieldType}`}#${r.wordIndex}`;
// The context before a word: its last 10 characters, so the word itself
// stays in view on a one-line row (the full context is in the row's hint).
const CTX_TAIL = 10;
const ctxTail = (before) => String(before || '').slice(-CTX_TAIL);

const FIELD_LABEL = { description: 'description', after_message: 'after message' };

export default function SelectionPopup({
  at, questId, wordCount, fragPlan, take, word,
  onVoice, onPlay, onClose, onGlossaryApplied, onVerdictChanged, onReadingChanged,
}) {
  const root = useRef(null);
  const audio = useRef(null);
  const stopAt = useRef(null);
  const [playing, setPlaying] = useState(null);

  const [entry, setEntry] = useState(undefined);   // undefined = loading, null = absent
  const [stressed, setStressed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState(null);
  const [error, setError] = useState(null);

  const [rows, setRows] = useState(null);          // null = loading
  const [rowsError, setRowsError] = useState(null);
  const [tick, setTick] = useState(0);             // re-read the occurrences

  const bare = word ? word.bare : null;
  const plain = word ? stripAcutes(word.spelling) : '';

  // Who reads the fragment: the text's own speaker unless switched here. Every
  // new selection starts over from the text — the switch is for one take.
  const textSpeaker = fragPlan ? speakerOfPrefix(fragPlan.prefix) : null;
  const [speaker, setSpeaker] = useState(textSpeaker || 1);
  useEffect(() => { setSpeaker(textSpeaker || 1); }, [fragPlan, textSpeaker]);
  // Left as the text has it, the plan goes out untouched — a text with no
  // labels at all gets none added.
  const plan = fragPlan && speaker !== (textSpeaker || 1) ? withSpeaker(fragPlan, speaker) : fragPlan;

  // Close on Esc or on a click that lands outside — but a click in the prose
  // is how the next selection is made, so that one only moves the popup.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e) => {
      if (root.current && root.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.as-text')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  // The glossary entry for this form, and the mark to start from: the one
  // this place is read with now (its own, or the glossary's).
  useEffect(() => {
    if (!bare) { setEntry(null); return undefined; }
    let dead = false;
    setEntry(undefined);
    setSaveNote(null);
    fetch(`/api/pipeline/quest/${questId}/glossary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Could not read the glossary'))))
      .then((d) => {
        if (dead) return;
        const found = (d.glossary || []).find((g) => stripAcutes(String(g.form || '')).toLowerCase() === bare);
        setEntry(found || null);
        setStressed(word.spelling !== plain ? word.spelling : null);
      })
      .catch((e) => { if (!dead) { setEntry(null); setError(e.message); } });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bare, questId]);

  // Every place this form occurs in the quest, with its seconds and verdict.
  useEffect(() => {
    if (!bare) { setRows(null); return undefined; }
    let dead = false;
    setRows(null);
    setRowsError(null);
    fetch(`/api/pipeline/quest/${questId}/word-occurrences?form=${encodeURIComponent(bare)}`,
      { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Could not look the word up'))))
      .then((d) => { if (!dead) setRows(d.occurrences || []); })
      .catch((e) => { if (!dead) { setRows([]); setRowsError(e.message); } });
    return () => { dead = true; };
  }, [bare, questId, tick]);

  const playRow = useCallback((r) => {
    const el = audio.current;
    if (!el || !r.playable) return;
    if (playing === rowKey(r)) { el.pause(); setPlaying(null); return; }
    const src = `/api/audio-alignment/${r.versionId}/audio`;
    stopAt.current = r.end + 0.12;
    const go = () => {
      try { el.currentTime = Math.max(0, r.start - 0.12); } catch (e) { /* not seekable yet */ }
      el.play().catch(() => {});
    };
    setPlaying(rowKey(r));
    if (el.getAttribute('data-src') !== src) {
      el.setAttribute('data-src', src);
      el.src = src;
      el.addEventListener('loadedmetadata', go, { once: true });
      el.load();
    } else go();
  }, [playing]);

  const onTime = () => {
    const el = audio.current;
    if (!el || stopAt.current == null) return;
    if (el.currentTime >= stopAt.current) { el.pause(); setPlaying(null); }
  };

  const setVerdict = async (r, ok) => {
    const want = r.verdict === (ok ? 'ok' : 'wrong') ? null : ok;
    setRows((rs) => rs.map((x) => (rowKey(x) === rowKey(r)
      ? { ...x, verdict: want === null ? null : (want ? 'ok' : 'wrong'), verdictStale: false } : x)));
    try {
      const res = await fetch('/api/word-verdict', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionId: r.versionId, wordIndex: r.wordIndex, form: r.form, spelling: r.spelling, ok: want,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      if (onVerdictChanged) onVerdictChanged();
    } catch (e) {
      setRowsError(e.message);
      setTick((n) => n + 1);
    }
  };

  // Into the glossary: the rule for every place of this form that carries no
  // mark of its own. Nothing is written into the texts — they are read through
  // the glossary, and the studio re-reads them when the save is announced.
  const saveToGlossary = async () => {
    if (!word) return;
    setSaving(true);
    setError(null);
    setSaveNote(null);
    try {
      const res = await fetch(`/api/pipeline/quest/${questId}/glossary`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upserts: [{ form: plain, stressed, status: 'edited', note: (entry && entry.note) || '' }],
          deletes: [],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setEntry((data.glossary || []).find((g) => stripAcutes(String(g.form || '')).toLowerCase() === bare) || null);
      setSaveNote('saved — places without a mark of their own read it this way');
      setTick((n) => n + 1);
      if (onGlossaryApplied) onGlossaryApplied();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // This one place only: its own mark goes into the text. A mark equal to the
  // glossary's — or none — leaves the place to the glossary again.
  const stressHere = async (mark) => {
    if (!word || !word.place) return;
    setSaving(true);
    setError(null);
    setSaveNote(null);
    try {
      const res = await fetch(`/api/pipeline/quest/${questId}/voiceover-stress`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...word.place, stressed: mark || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSaveNote(data.changed ? 'this place is re-marked — re-voice it to hear it' : 'this place already read so');
      setTick((n) => n + 1);
      if (onReadingChanged) onReadingChanged();
      if (onGlossaryApplied) onGlossaryApplied();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const ruled = (entry && entry.stressed) || null;
  const dirty = !!word && !sameMarks(ruled, stressed);
  // What this place would read after «Only here», against what it reads now.
  const hereWouldRead = !stressed || (ruled && sameMarks(stressed, ruled)) ? ruled : stressed;
  const hereDirty = !!word && !!word.place && !sameMarks(hereWouldRead, word.spelling);
  const exact = useMemo(() => (rows || []).filter((r) => r.exact), [rows]);
  const near = useMemo(() => (rows || []).filter((r) => !r.exact), [rows]);
  const tally = useMemo(() => {
    const ok = exact.filter((r) => r.verdict === 'ok').length;
    const wrong = exact.filter((r) => r.verdict === 'wrong').length;
    return { ok, wrong, left: exact.length - ok - wrong };
  }, [exact]);

  // Dragged by its header, the panel stays where it was dropped for as long
  // as it is open: a new selection meanwhile no longer brings it back over the
  // text being read. Closing it forgets the spot.
  const [dropped, setDropped] = useState(null);    // {left, top} or null
  const drag = useRef(null);                       // {dx, dy} while dragging

  // Placed against the selection: below it when it fits, above it otherwise —
  // a selection in the last paragraph would open a panel nobody can see. The
  // height available on the chosen side becomes the panel's own limit, and the
  // measurement repeats as the occurrence list arrives and changes its size.
  // Once dropped somewhere, it is kept there instead, inside the window.
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    const place = () => {
      if (drag.current) return;   // the pointer owns the position meanwhile
      const gap = 8;
      let next;
      if (dropped) {
        const left = Math.max(gap, Math.min(dropped.left, window.innerWidth - el.offsetWidth - gap));
        const top = Math.max(gap, Math.min(dropped.top, window.innerHeight - 40));
        const maxH = Math.max(160, Math.round(window.innerHeight - top - gap));
        next = { maxHeight: `${maxH}px`, top: `${Math.round(top)}px`, left: `${Math.round(left)}px` };
      } else {
        const below = window.innerHeight - at.bottom - gap * 2;
        const above = at.top - gap * 2;
        const wanted = el.scrollHeight;
        const up = below < Math.min(wanted, 220) && above > below;
        const maxH = Math.max(160, Math.round(up ? above : below));
        const h = Math.min(wanted, maxH);
        const top = up ? Math.max(gap, at.top - h - gap)
          : Math.min(at.bottom + gap, Math.max(gap, window.innerHeight - h - gap));
        const left = Math.max(gap, Math.min(at.x, window.innerWidth - el.offsetWidth - gap));
        next = { maxHeight: `${maxH}px`, top: `${Math.round(top)}px`, left: `${Math.round(left)}px` };
      }
      Object.entries(next).forEach(([k, v]) => { if (el.style[k] !== v) el.style[k] = v; });
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    window.addEventListener('resize', place);
    return () => { ro.disconnect(); window.removeEventListener('resize', place); };
  }, [at, dropped]);

  // The header is the handle; its close button is not. The panel follows the
  // pointer directly (no re-render per move), and the spot is kept on release.
  const onHeadDown = (e) => {
    if (e.button !== 0 || e.target.closest('button') || !root.current) return;
    const box = root.current.getBoundingClientRect();
    drag.current = { dx: e.clientX - box.left, dy: e.clientY - box.top };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onHeadMove = (e) => {
    const el = root.current;
    if (!drag.current || !el) return;
    const gap = 8;
    const left = Math.max(gap, Math.min(e.clientX - drag.current.dx, window.innerWidth - el.offsetWidth - gap));
    const top = Math.max(gap, Math.min(e.clientY - drag.current.dy, window.innerHeight - 40));
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  };
  const onHeadUp = () => {
    const el = root.current;
    if (!drag.current || !el) return;
    drag.current = null;
    setDropped({ left: parseFloat(el.style.left) || 0, top: parseFloat(el.style.top) || 0 });
  };

  // What "voice selection only" will send to Google, as the server plans it
  // for the take itself — the same request body the take goes out with
  // (AudioStudio.newTake), so the line below can't promise something else.
  const previewBody = useMemo(() => (plan
    ? buildTtsRequest({ text: plan.text, settings: loadLastTtsSettings(), questId })
    : null), [plan, questId]);
  const preview = useTtsPreview(previewBody);
  const reqs = preview.data ? preview.data.requests : [];
  const sentLines = reqs.map((r) => `${slotsLabel(r)}: “${r.text}”`);
  let sent = 'Voices as: …';
  if (preview.error) sent = `Could not get what will be sent: ${preview.error}`;
  else if (reqs.length === 1) sent = `Voices as ${sentLines[0]}`;
  else if (reqs.length > 1) sent = `${reqs.length} requests — ${sentLines.join(' · ')}`;
  // The settings the take will run with — read off the very body it is sent
  // with, so this line can't promise something else either.
  const params = previewBody ? [
    previewBody.model,
    previewBody.scheme === 'whole' ? 'whole take in one request' : 'one request per turn',
    `temperature ${previewBody.temperature}`,
    `${previewBody.voices.speaker1} / ${previewBody.voices.speaker2}`,
  ].join(' · ') : '';
  // What steers the delivery: the prompt for 3.1 and older, each speaker's
  // style for 3.8 (see ttsRequestFormat on the server).
  const steering = preview.data
    ? (preview.data.format === 'prompt'
      ? [['Prompt', preview.data.systemPrompt || '']]
      : reqs.flatMap((r) => (r.parts || []).map((pt) => [`Style, ${pt.speaker}`, pt.style || '(none)'])))
    : [];

  const style = { left: Math.max(8, Math.min(at.x, window.innerWidth - 430)), top: at.bottom + 8 };

  const renderRows = (list) => list.map((r) => {
    const key = rowKey(r);
    return (
      <div key={key} className={`sp-row${r.verdict === 'wrong' ? ' sp-row-wrong' : ''}`}>
        <button
          type="button"
          className="sp-play"
          disabled={!r.playable}
          onClick={() => playRow(r)}
          title={r.playable ? `Play this place from the active version (${fmt(r.start)})` : 'This take was voiced from other words — no timings here'}
        >{playing === key ? '⏸' : '▶'}</button>
        <span className="sp-where">
          #{r.pointNo} {r.locationName} · {FIELD_LABEL[r.fieldType] || r.fieldType}
          {r.onlyHere ? <span className="sp-own-tag" title="This place has a stress mark of its own"> · only here</span> : null}
        </span>
        <span className="sp-ctx" title={`…${r.before}${r.spelling}${r.after}…`}>
          …{ctxTail(r.before)}<b>{r.spelling}</b>{r.after}…
        </span>
        <span className="sp-verdict">
          <button
            type="button"
            className={`sp-mark${r.verdict === 'ok' ? ' sp-mark-on' : ''}`}
            disabled={!r.playable}
            title={r.verdict === 'ok'
              ? 'Click again to remove this mark.'
              : 'Mark that you listened to this place and the word is said right. It is only a mark: nothing is voiced and the text does not change. Click again to remove it.'}
            onClick={() => setVerdict(r, true)}
          >✓</button>
          <button
            type="button"
            className={`sp-mark${r.verdict === 'wrong' ? ' sp-mark-on' : ''}`}
            disabled={!r.playable}
            title={r.verdict === 'wrong'
              ? 'Click again to remove this mark.'
              : 'Mark that the word is said wrong here. The point gets a ✗ count with the number of such places in the list of points on the left. It is only a mark: nothing is voiced and the text does not change. Click again to remove it.'}
            onClick={() => setVerdict(r, false)}
          >✗</button>
        </span>
        {r.verdictStale ? (
          <span className="sp-stale" title="Judged when the text spelled this word differently — listen again">↻</span>
        ) : null}
      </div>
    );
  });

  return (
    <div className="sp-root" ref={root} style={style} role="dialog">
      <div
        className="sp-head"
        title="Drag to move the panel"
        onPointerDown={onHeadDown}
        onPointerMove={onHeadMove}
        onPointerUp={onHeadUp}
        onLostPointerCapture={onHeadUp}
      >
        <strong>{wordCount === 1 ? '1 word' : `${wordCount} words`} selected</strong>
        <button type="button" className="sp-close" onClick={onClose} title="Close (Esc)">✕</button>
      </div>

      <div className="sp-actions">
        <button type="button" onClick={onPlay}>▶ Play selection</button>
        <button
          type="button"
          className="sp-primary"
          onClick={() => onVoice(plan)}
          disabled={!plan || (take && take.status === 'running')}
          title={plan
            ? `Generate a voiceover of the selected words only, read by Speaker ${speaker}; the take is never made active`
            : 'These words are no longer in the text of the point'}
        >
          <StableLabel
            current={take && take.status === 'running' && take.fragment
              ? `Generating voiceover… ${take.elapsed || 0}s` : '+ Generate voiceover for selection'}
            sizes={['+ Generate voiceover for selection', 'Generating voiceover… 000s']}
          />
        </button>
      </div>
      {fragPlan ? (
        <div className="sp-speaker">
          <span>Read by</span>
          {[1, 2].map((n) => (
            <button
              key={n}
              type="button"
              className={`sp-mark${speaker === n ? ' sp-mark-on' : ''}`}
              onClick={() => setSpeaker(n)}
              disabled={take && take.status === 'running'}
              title={n === textSpeaker
                ? `Speaker ${n} — the one the text gives these words to`
                : textSpeaker == null && n === 1
                  ? 'As the full take reads it: the text has no speaker labels, none is added'
                  : `Speaker ${n} — the other voice from the generation panel; the text has ${textSpeaker == null ? 'no labels, so «Speaker ' + n + ': [firmly]» is put in front' : 'these words under Speaker ' + textSpeaker}`}
            >
              Speaker {n}{n === textSpeaker ? ' (text)' : ''}
            </button>
          ))}
        </div>
      ) : null}
      {plan ? (
        // What Google will actually get, as the server plans it: the turn's
        // voice, the label and the glossary's stress marks included.
        <>
          <div className="sp-preview" title={sentLines.join('\n') || sent}>{sent}</div>
          <div className="sp-params">{params}</div>
          {steering.length ? (
            <details className="sp-steering">
              <summary>{steering.map(([h]) => h).join(', ')}</summary>
              {steering.map(([h, v], i) => (
                <div key={i}><div className="sp-steering-h">{h}</div><pre>{v}</pre></div>
              ))}
            </details>
          ) : null}
        </>
      ) : (
        <div className="sp-warn">These words are no longer in the text of the point.</div>
      )}

      {word ? (
        <>
          <div className="sp-sep" />
          <div className="sp-word">
            <span className="sp-letters">
              {plain.split('').map((ch, i) => (
                <button
                  key={i}
                  type="button"
                  className={`sp-letter${isVowel(ch) ? ' sp-letter-vowel' : ''}`}
                  disabled={!isVowel(ch)}
                  title={isVowel(ch) ? 'Put the stress here' : undefined}
                  onClick={() => setStressed(toggleAcuteAt(stressed, plain, i))}
                >{ch}</button>
              ))}
            </span>
            <span className="sp-stressed">{stressed || '∅ no mark'}</span>
            <button
              type="button"
              disabled={saving || !hereDirty || entry === undefined}
              onClick={() => stressHere(stressed)}
              title={!word.place ? 'This word is no longer in the text of the point'
                : !stressed && ruled ? 'A place with no mark of its own follows the glossary'
                  : 'Stress this one place so; the glossary and every other place stay as they are'}
            >Only here</button>
            <button
              type="button"
              className="sp-primary"
              disabled={saving || !dirty || entry === undefined}
              onClick={saveToGlossary}
              title="The quest's rule: every place of this form without a mark of its own is read so"
            >
              <StableLabel
                current={saving ? 'Saving…' : (entry ? 'Update glossary' : 'Add to glossary')}
                sizes={['Add to glossary', 'Update glossary', 'Saving…']}
              />
            </button>
          </div>
          <div className="sp-glossary-state">
            {entry === undefined ? 'reading the glossary…'
              : entry ? `in the glossary: ${entry.stressed || '∅ no mark'}${entry.status === 'edited' ? ' · ✎ edited' : ' · auto'}`
                : 'not in the glossary yet'}
            {saveNote ? ` · ${saveNote}` : ''}
          </div>
          {word.ownMark ? (
            <div className="sp-glossary-state">
              <span className="sp-own-tag">this place: {word.ownMark} — only here</span>
              {' · '}
              <button
                type="button"
                className="sp-mark"
                disabled={saving}
                onClick={() => stressHere(null)}
                title="Take this place's own mark off; it is read the glossary's way again"
              >{ruled ? `↺ follow the glossary (${ruled})` : '↺ take the mark off'}</button>
            </div>
          ) : null}
          {error ? <div className="sp-warn">{error}</div> : null}

          <div className="sp-sep" />
          <div className="sp-list-head">
            Across the quest
            {rows === null ? ' · looking…' : ` · ${exact.length} place(s)`}
            {rows && exact.length ? ` · ${tally.ok} ✓ · ${tally.wrong} ✗ · ${tally.left} not heard` : ''}
          </div>
          {rowsError ? <div className="sp-warn">{rowsError}</div> : null}
          <div className="sp-list">
            {rows === null ? <div className="sp-note">⏳</div> : null}
            {rows && !rows.length ? <div className="sp-note">This form occurs nowhere else.</div> : null}
            {renderRows(exact)}
            {near.length ? <div className="sp-list-head sp-near">Other forms · {near.length}</div> : null}
            {renderRows(near)}
          </div>
          <audio ref={audio} preload="none" onTimeUpdate={onTime} onEnded={() => setPlaying(null)} />
        </>
      ) : null}
    </div>
  );
}
